import { useEffect, useRef, useState } from "react"
import { RootState } from "../../../App/store"
import { useDispatch, useSelector } from "react-redux"
import { ConversationMessageGetRequest, ConversationMessageSeenPostRequest } from "../ChatServiceRequest"
import ChatMessages from "./ChatMessages"
import { Conversation, ConversationMessage } from "../../../Types/Conversation"
import { LoadingAnimation } from "../../Shared/Components/SvgIcons"
import { setConversationList } from "../ChatSlice"
import { useAppSocket } from "../../Shared/Socket/AppSocketProvider"

function ChatContent(props: { sentMsg: ConversationMessage | undefined, editedMsg: ConversationMessage | undefined, onStartEdit: (message: ConversationMessage) => void }) {
    const activeConversationId = useSelector((state: RootState) => state.chatSlice.activeConversationId)
    const conversations = useSelector((state: RootState) => state.chatSlice.conversations)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadingConversation, setloadingConversation] = useState(true)
    const scrollContainer = useRef<HTMLDivElement>(null)
    const [messages, setMessages] = useState<Array<ConversationMessage>>([])
    const bottom = useRef<HTMLDivElement>(null)
    const container = useRef<HTMLDivElement>(null)
    const conversationsRef = useRef(conversations)
    const pendingSeenMessagesRef = useRef<number[]>([])
    const lastSeenBatchKeyRef = useRef("")
    const dispatch = useDispatch()
    const { sendJson, isConnected } = useAppSocket()

    const markSeenHttpFallback = (conversationId: number, messageIds: number[]) => {
        if (conversationId < 1 || messageIds.length === 0) {
            return
        }

        ConversationMessageSeenPostRequest(conversationId, messageIds).catch((error) => {
            console.log(error)
        })
    }

    useEffect(() => {
        conversationsRef.current = conversations
    }, [conversations])

    useEffect(() => {
        if (activeConversationId > 0) {
            setloadingConversation(true)
            setPageNum(1)

            const controller = new AbortController()
            const { signal } = controller
            ConversationMessageGetRequest(activeConversationId!, 1)
                .then(response => {
                    if (response.status === 200) {
                        setMessages(response.data.results.reverse())
                        setHasNextPage(Boolean(response.data.next))
                        setloadingConversation(false)
                        var topPos = bottom.current!.offsetTop
                        container.current!.scrollTop = topPos - 10
                        
                        let newList = conversationsRef.current.map((item) =>  {
                            if(item.id === activeConversationId){
                                const updatedItem = {
                                    ...item,
                                    unseen_messages: 0,
                                  };
                          
                                  return updatedItem;
                            }
                
                            return item;
                        })
                        
                        dispatch(setConversationList(newList))
                    }
                })
                .catch(error => {
                    if (signal.aborted) return
                    console.log(error)
                })

            return () => {
                setPageNum(1)
                controller.abort()
            }
        }
    }, [activeConversationId, dispatch])

    const onScroll = () => {
        if (scrollContainer.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current
            if (Math.ceil(scrollTop * -1 + clientHeight + 1) >= scrollHeight && hasNextPage && !loadingConversation) {
                const controller = new AbortController()
                const { signal } = controller

                ConversationMessageGetRequest(activeConversationId!, pageNum + 1)
                    .then(response => {
                        if (response.status === 200) {
                            setMessages(response.data.results.reverse().concat(messages))
                            setHasNextPage(Boolean(response.data.next))
                            var topPos = bottom.current!.offsetTop
                            container.current!.scrollTop = topPos - 10
                            setPageNum(pageNum + 1)
                        }
                    })
                    .catch(error => {
                        if (signal.aborted) return
                        console.log(error)
                    })
                return () => controller.abort()
            }
        }
    }

    // Update message status after messages are rendered.
    // On mobile/iOS websocket connect can lag, so we queue and retry after connect.
    useEffect(() => {
        let msgs_to_update = messages.filter(msg => msg.author.id !== userDataState.id).map(msg => msg.id)
        if (msgs_to_update.length === 0) {
            pendingSeenMessagesRef.current = []
            return
        }

        const batchKey = msgs_to_update.join(",")
        if (batchKey === lastSeenBatchKeyRef.current) {
            return
        }

        pendingSeenMessagesRef.current = msgs_to_update
        const markBatchAsHandled = () => {
            lastSeenBatchKeyRef.current = batchKey
            pendingSeenMessagesRef.current = []
        }

        if (!isConnected) {
            markSeenHttpFallback(activeConversationId, msgs_to_update)
            markBatchAsHandled()
            return
        }

        const sent = sendJson({ type: "msg.seen", messages: msgs_to_update })
        if (sent) {
            markBatchAsHandled()
        } else {
            markSeenHttpFallback(activeConversationId, msgs_to_update)
            markBatchAsHandled()
        }
    }, [messages, conversations, dispatch, isConnected, sendJson, userDataState.id])

    useEffect(() => {
        if (!isConnected || pendingSeenMessagesRef.current.length === 0) {
            return
        }

        const msgs_to_update = pendingSeenMessagesRef.current
        const batchKey = msgs_to_update.join(",")
        if (batchKey === lastSeenBatchKeyRef.current) {
            return
        }

        const markBatchAsHandled = () => {
            lastSeenBatchKeyRef.current = batchKey
            pendingSeenMessagesRef.current = []
        }

        const sent = sendJson({ type: "msg.seen", messages: msgs_to_update })
        if (sent) {
            markBatchAsHandled()
        } else {
            markSeenHttpFallback(activeConversationId, msgs_to_update)
            markBatchAsHandled()
        }
    }, [activeConversationId, conversations, dispatch, isConnected, sendJson])

    useEffect(() => {
        if (props.sentMsg!) {
            if (activeConversationId === props.sentMsg.conversation.id) {
                setMessages((currentMessages) => [...currentMessages, props.sentMsg!])
                setTimeout(() => {
                    var topPos = bottom.current!.offsetTop
                    container.current!.scrollTop = topPos - 10
                }, 10)
            }
        }
    }, [props.sentMsg, activeConversationId])

    useEffect(() => {
        if (props.editedMsg && activeConversationId === props.editedMsg.conversation.id) {
            setMessages((currentMessages) => currentMessages.map((message) => {
                if (message.id === props.editedMsg!.id) {
                    return {
                        ...message,
                        ...props.editedMsg,
                        is_edited: props.editedMsg?.is_edited ?? true,
                    }
                }

                return message
            }))
        }
    }, [activeConversationId, props.editedMsg])

    return (
        <div className="chat-layout__right-panel-content" ref={container}>
            <div className="chat-layout__msg-container" onScroll={onScroll} ref={scrollContainer}>
                <div style={{ width: "100%" }}>
                    {loadingConversation === true ? <LoadingAnimation type="cover" /> : <ChatMessages msgarray={messages} onStartEdit={props.onStartEdit} />}
                    <div style={{ float: "left", clear: "both" }} ref={bottom}></div>
                </div>
            </div>
        </div>
    )
}
export default ChatContent
